import type { ListBuilderColumn, ListBuilderConfiguration } from '../types';

/**
 * Driver list configuration
 * Converted from JSON to TypeScript for type safety.
 *
 * Multi-view list with 3 tabs: All Drivers, Available, On a Trip — driven by
 * the derived `driverTripStatus` (whether the driver is currently out on an
 * in-progress / pending-driver-confirm trip). The old active/inactive "Status"
 * axis was retired: its filter referenced `u.isActive` in a HAVING clause and
 * threw "Unknown column 'u.isActive' in 'having clause'". Mirrors the vehicle
 * Trip Status tabs (see configs/vehicle/).
 */

/**
 * Shared driver columns, reused across all three tabs so the column set stays
 * in lockstep. The "Trip Status" column reads the derived `tripStatus` field
 * the backend returns (`on-trip` | `available`).
 */
const createDriverColumns = (): Array<ListBuilderColumn> => [
	{
		id: 'driver-name',
		headers: [
			{
				id: 'driver-name-header',
				text: 'Driver Name',
			},
		],
		fields: [
			{
				id: 'driver-name-field',
				dataConfig: {
					type: 'template',
					template: '{driver.firstName} {driver.lastName}',
					templateFields: [],
				},
				styleConfig: {
					size: 'md',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'text',
				link: {
					type: 'object',
					enabled: true,
				},
			},
		],
		sortable: true,
	},
	{
		id: 'trip-status',
		headers: [
			{
				id: 'trip-status-header',
				text: 'Trip Status',
			},
		],
		fields: [
			{
				id: 'trip-status-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'activeTrip',
						objectTable: 'driver',
					},
				},
				// PD-13747 — the static on-trip/available badge is now
				// status-aware. The shared `trip-status-badge` custom cell reads
				// the row's `activeTrip` overlay and renders the live trip status
				// ("In Progress", "Paused · Meal", "Returning to warehouse") via
				// getTripStatusPresentation, falling back to "Available" when the
				// driver has no active trip. The `driverTripStatus` filter tabs
				// still drive on-trip/available.
				displayStyle: 'custom',
				customComponent: {
					id: 'trip-status-badge',
				},
			},
		],
		sortable: true,
		width: 120,
	},
	{
		id: 'assigned-routes',
		headers: [
			{
				id: 'assigned-routes-header',
				text: 'Assigned Routes',
			},
		],
		fields: [
			{
				id: 'assigned-routes-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'assignedRoutesCount',
						objectTable: 'driver',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'text',
			},
		],
		sortable: true,
		width: 140,
	},
	{
		id: 'total-deliveries',
		headers: [
			{
				id: 'total-deliveries-header',
				text: 'Total Deliveries',
			},
		],
		fields: [
			{
				id: 'total-deliveries-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'totalDeliveries',
						objectTable: 'driver',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'text',
			},
		],
		sortable: true,
		width: 140,
	},
	{
		id: 'on-time-percentage',
		headers: [
			{
				id: 'on-time-percentage-header',
				text: 'On-Time %',
			},
		],
		fields: [
			{
				id: 'on-time-percentage-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'onTimePercentage',
						objectTable: 'driver',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
					// `decimalPlaces: 0` keeps the cell tidy as "75%"
					// rather than "75.00%" (the formatter's default).
					// The detail card uses the same precision, so
					// list + detail now match.
					formatOptions: {
						decimalPlaces: 0,
					},
				},
				// D008 fix (2026-05-26 E2E pass): was 'text' which
				// rendered "0" — inconsistent with the driver detail
				// card showing "0%". Using 'percent' lets the
				// list-builder's number formatter append the % suffix
				// (see csvFieldExtractor.ts).
				displayStyle: 'percent',
			},
		],
		sortable: true,
		width: 120,
	},
	{
		// D007 (2026-05-26): "Recent Trips" = trips driven in
		// the last 14 days. Surfaces driver recency on the
		// list view; full detail tab adds tooltips/context.
		id: 'recent-trips-count',
		headers: [
			{
				id: 'recent-trips-count-header',
				text: 'Trips (14d)',
			},
		],
		fields: [
			{
				id: 'recent-trips-count-field',
				dataConfig: {
					type: 'field',
					field: {
						fieldName: 'recentTripsCount',
						objectTable: 'driver',
					},
				},
				styleConfig: {
					size: 'sm',
					colorRule: {
						type: 'default',
					},
				},
				displayStyle: 'text',
			},
		],
		sortable: true,
		width: 120,
	},
];

export const DRIVER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-drivers',
			name: 'All Drivers',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			columns: createDriverColumns(),
			defaultSortId: 'driver-name',
			sortOrder: 0,
		},
		{
			id: 'default-drivers-available',
			name: 'Available',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			filterRules: {
				driverTripStatus: {
					defaultValue: [{ id: 'available', label: 'Available' }],
				},
			},
			columns: createDriverColumns(),
			defaultSortId: 'driver-name',
			sortOrder: 1,
		},
		{
			id: 'default-drivers-on-trip',
			name: 'On a Trip',
			type: 'default',
			active: true,
			cacheTTL: 600000,
			filterRules: {
				driverTripStatus: {
					defaultValue: [{ id: 'on-trip', label: 'On a Trip' }],
				},
			},
			columns: createDriverColumns(),
			defaultSortId: 'driver-name',
			sortOrder: 2,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: ['driverSearch', 'driverTripStatus'],
		defaultFilters: {},
	},
	// The lists above declare `defaultSortId: 'driver-name'`, so the
	// sort resolver expects a matching tableSort entry. Without this,
	// ListBuilder logs "defaultSortId 'driver-name' did not resolve to
	// any sort fields" and falls back to a usable sort. Mirrors the
	// 2026-05-26 fix on the vehicles list (task #45). Driver name on
	// the column is a `{driver.firstName} {driver.lastName}` template,
	// so the canonical sort is lastName→firstName (directory ordering).
	tableSorts: [
		{
			id: 'driver-name',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'lastName', objectTable: 'driver' },
				{ direction: 'ASC', fieldName: 'firstName', objectTable: 'driver' },
			],
			description: 'Sort drivers alphabetically by last name, then first',
		},
		{
			id: 'driver-name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'lastName', objectTable: 'driver' },
				{ direction: 'DESC', fieldName: 'firstName', objectTable: 'driver' },
			],
			description:
				'Sort drivers reverse-alphabetically by last name, then first',
		},
		{
			id: 'trip-status-asc',
			name: 'Trip Status (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'tripStatus', objectTable: 'driver' },
				{ direction: 'ASC', fieldName: 'lastName', objectTable: 'driver' },
			],
			description:
				'Group drivers by trip status, then alphabetical by last name',
		},
	],
	objects: [
		{
			baseUrl: '/distribution/drivers/:id',
			objectLabel: 'Driver',
			objectTable: 'driver',
			selectedFields: [
				{
					fieldName: '_id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'firstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'lastName',
					fieldLabel: 'Last Name',
				},
				{
					fieldName: 'phoneNumber',
					fieldLabel: 'Phone',
				},
				{
					fieldName: 'email',
					fieldLabel: 'Email',
				},
				{
					fieldName: 'tripStatus',
					fieldLabel: 'Trip Status',
				},
				{
					// PD-13747 — the live active-trip status overlay (pause /
					// warehouse return) consumed by the trip-status-badge cell.
					fieldName: 'activeTrip',
					fieldLabel: 'Trip Status',
				},
				{
					fieldName: 'assignedRoutesCount',
					fieldLabel: 'Assigned Routes',
				},
				{
					fieldName: 'totalDeliveries',
					fieldLabel: 'Total Deliveries',
				},
				{
					fieldName: 'onTimePercentage',
					fieldLabel: 'On-Time Percentage',
				},
				{
					// D008 (2026-05-26): denominator for on-time%. Number
					// of stops that had an ETA to evaluate against.
					fieldName: 'onTimeEligibleDeliveries',
					fieldLabel: 'On-Time Eligible Deliveries',
				},
				{
					// D007 (2026-05-26): 14-day trip count (recency).
					fieldName: 'recentTripsCount',
					fieldLabel: 'Trips (Last 14 Days)',
				},
				{
					// D008 (2026-05-26): avg late-departure offset.
					fieldName: 'averageStartDelayMinutes',
					fieldLabel: 'Avg Start Delay (min)',
				},
				{
					fieldName: 'createdDate',
					fieldLabel: 'Created Date',
				},
				{
					fieldName: 'modifiedDate',
					fieldLabel: 'Modified Date',
				},
			],
		},
	],
};
